import axios from "axios";
import { normalizeProjectAddressConflict } from "./projectAddressConflict";
import { changesSubcontractorDirectory, notifySubcontractorDirectoryChanged } from "./subcontractorDirectoryEvents";
import { trackUpload, labelFromFormData, sizeFromFormData } from "./uploadProgress";
import { sanitizeDetail } from "./detailText";

/**
 * 上传失败原因的文字化（浮层展示用）。
 *
 * 为什么单独写一份而不是复用 lib/upload.js 的 uploadErrText：
 *   那个模块是分片上传器（依赖 imageShrink / uploadQueue），api.js 是几乎所有模块的
 *   最底层依赖，反过来 import 会形成循环依赖。这里只需要「把 detail 变成一句话」，
 *   逻辑很小，就地实现更安全。
 * 与 uploadErrText 保持同一口径：对象 detail 优先取 message，其次兜底通用文案。
 *
 * 2026-10-23 补「网络层失败」映射（背景：Jerry 反馈现场快照拖拽上传失败，右上角浮层
 * 直接亮出 axios 原文「Network Error」，被老板当作新 Bug 上报）。排查结论：请求在
 * 网络层就没拿到响应（他的出口链路把带包体的 POST 掐断，后端日志里连一条失败记录
 * 都没有），不是服务器拒绝、也不是权限或格式问题。lib/upload.js 的 uploadErrText
 * 早已把这类错误翻成中文人话，但浮层这条路没走到它——两个文案口径不一致。
 * 这里按同样规则补齐：取消 → 已取消；无响应状态（含 Network Error）→ 网络中断提示；
 * 5xx → 服务器暂不可用。文案与 uploadErrText 逐字一致，同一处失败两边提示相同。
 */
const uploadErrorText = (e) => {
  if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError") return "已取消上传";
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || d.error || "上传失败";
  if (d) return d;
  const status = e?.response?.status;
  if (!status || e?.message === "Network Error") {
    return "网络连接中断，已自动重试仍未恢复；请检查网络后重试";
  }
  if (status >= 500) return `服务器暂时不可用（${status}），请稍后重试`;
  return e?.message || "上传失败";
};

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: BASE });
api.interceptors.request.use((config) => {
  config.headers["X-Lang"] = localStorage.getItem("ali_lang") || "zh";
  return config;
});

/**
 * 全局上传进度接管（2026-10-22 用户需求「上传时最好是屏幕右上角有一个上传进度」）。
 *
 * 为什么放在 axios 拦截器里，而不是逐个上传入口去加：
 *   全站上传（设计图纸 / 阶段任务卡 / City 结论 / 评论附件 / 协作 / 财务导入 / CRM）
 *   都走同一个 axios 实例，各入口原本只有零散甚至没有进度。在请求层统一接管后，
 *   任何 FormData 上传都会自动出现在右上角浮层里 —— 包括以后新增的入口，
 *   不需要谁记得去接进度，也不会再出现「某个页面点了上传毫无反应」。
 *
 * 关键取舍：
 *   · 只在 FormData 请求上挂：普通 JSON 请求不产生「上传进度」，避免噪音；
 *   · 调用方自己传了 onUploadProgress 的，两个都调用（保留原局部进度条的行为）；
 *   · 小文件延迟显示，详见 lib/uploadProgress.js。
 */
api.interceptors.request.use((config) => {
  const data = config.data;
  // FormData 在浏览器里是独立类型；这里做能力检测，避免非浏览器环境报错。
  const isForm = typeof FormData !== "undefined" && data instanceof FormData;
  // 扫描预处理有独立阶段提示，取消/重算不应冒充凭证归档上传失败。
  if (!isForm || config.skipUploadProgress) return config;

  const job = trackUpload({
    label: labelFromFormData(data),
    size: sizeFromFormData(data),
    url: String(config.url || ""),
  });
  // 把句柄挂到 config 上，响应拦截器据此收口（成功 / 失败都必须在同一条链上结束）。
  config.__uploadJob = job;

  const orig = config.onUploadProgress;
  config.onUploadProgress = (ev) => {
    job.update({ loaded: ev?.loaded || 0, total: ev?.total || 0 });
    if (typeof orig === "function") orig(ev);
  };
  return config;
});

export function setToken(token) {
  if (token) api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete api.defaults.headers.common["Authorization"];
}

// Auth storage: "remember me" → localStorage (persists); otherwise sessionStorage (cleared when the browser closes)
const SESSION_FLAG = "ali_session_only";
const pick = () => (sessionStorage.getItem(SESSION_FLAG) === "1" ? sessionStorage : localStorage);
export const authStore = {
  useSession(sessionOnly) { if (sessionOnly) sessionStorage.setItem(SESSION_FLAG, "1"); else sessionStorage.removeItem(SESSION_FLAG); },
  get: (k) => pick().getItem(k),
  set: (k, v) => pick().setItem(k, v),
  clear() { ["ali_token", "ali_user", "ali_imp_backup"].forEach((k) => { localStorage.removeItem(k); sessionStorage.removeItem(k); }); sessionStorage.removeItem(SESSION_FLAG); },
};

const saved = authStore.get("ali_token");
if (saved) setToken(saved);

/**
 * 当前**实际生效**的 token（给文件直链 `?auth=` 用）。
 *
 * 为什么要回退：`authStore.get("ali_token")` 只读 localStorage / sessionStorage，
 * 而「仅本次会话」登录、或本地存储被清过但会话仍有效时，存储里可能没有 token，
 * 但 axios 实例上的 Authorization 头是好的 —— 于是 `?auth=` 会拼成空串，
 * `<img src="...?auth=">` 被 Chrome 的 ORB 拦掉（ERR_BLOCKED_BY_ORB），表现为破图
 * （2026-10-15 用 Playwright 实测到的现象：上传、预览逻辑都对，但缩略图 401/被拦）。
 * 先读存储、再回退到 live 头，两条路都堵上。
 */
export const authToken = () => {
  const t = authStore.get("ali_token");
  if (t) return t;
  const h = api.defaults.headers.common && api.defaults.headers.common.Authorization;
  return typeof h === "string" ? h.replace(/^Bearer\s+/i, "") : "";
};

// 令牌过期 / 失效：任何受保护接口返回 401 时清掉本地会话并回到登录页（带回跳），避免页面停留在「已登录」外观但所有数据都为空（如新增客户选不了客户类型）
let redirecting = false;
api.interceptors.response.use((r) => {
  // 上传成功：收口右上角进度（小文件若从未露面则静默移除，见 uploadProgress）。
  r.config?.__uploadJob?.done();
  // 仅在保存成功后使候选列表失效；失败请求不会让其他页面误以为新增已经完成。
  if (changesSubcontractorDirectory(r.config)) notifySubcontractorDirectoryChanged();
  return r;
}, (error) => {
  // 上传失败：浮层转失败态并显示原因，随后自动收起；调用方原有的 toast 仍会照常弹出。
  error?.config?.__uploadJob?.fail(uploadErrorText(error));
  normalizeProjectAddressConflict(error);
  sanitizeDetail(error);
  const st = error?.response?.status;
  const url = String(error?.config?.url || "");
  if (st === 401 && !url.includes("/auth/login") && !url.startsWith("/public") && authStore.get("ali_token") && !redirecting && typeof window !== "undefined") {
    redirecting = true;
    authStore.clear();
    setToken(null);
    const path = window.location.pathname + window.location.search;
    if (!path.startsWith("/login")) window.location.assign(`/login?expired=1&next=${encodeURIComponent(path)}`);
  }
  // 【2026-10-24 权限同源改造】403 全局轻提示：过去 403 全部下放给调用方——约 44 处显式处理、
  // 270+ 处静默吞掉，「接口被拒」经常表现为页面某处默默不出数据，用户分不清是没数据、
  // 没权限还是坏了，管理员也收不到任何信号。现统一弹一次后端 detail 原文（3.5s 自动消失）；
  // 同一文案 3 秒内只提示一次，避免批量请求逐条 403 时刷屏。调用方对 403 的自有处理照旧。
  if (st === 403 && !url.startsWith("/auth/") && typeof window !== "undefined") {
    const detail = error?.response?.data?.detail;
    const msg = (typeof detail === "string" && detail) ? detail : (localStorage.getItem("ali_lang") === "en" ? "No permission for this action" : "暂无该操作权限，请联系管理员在「角色管理 / 账号列表」开通");
    const now = Date.now();
    if (now - (globalThis.__last403ToastAt?.[msg] || 0) > 3000) {
      (globalThis.__last403ToastAt = globalThis.__last403ToastAt || {})[msg] = now;
      import("../components/ui/sonner").then(({ toast }) => toast.info(msg, { duration: 3500 })).catch(() => {});
    }
  }
  return Promise.reject(error);
});

export default api;
